import { useState, useEffect, useRef, useCallback } from 'react'
import { useSettingsStore } from '@/stores/settingsStore'

const LOCK_TIMEOUT_MS = 60_000 // 1분 비활성 후 잠금
const CREDENTIAL_STORAGE_KEY = 'tdl_applock_credential_id'

/**
 * 앱 잠금 — visibilitychange 감지 → WebAuthn 생체인증
 *
 * 플로우:
 *   1. 설정에서 appLockEnabled 활성화 → registerCredential() 호출하여 지문/얼굴 등록
 *   2. 앱 1분 이상 비활성 → isLocked=true
 *   3. unlock() 호출 → 등록된 credential로 생체인증 검증
 */
export function useAppLock() {
  const appLockEnabled = useSettingsStore((s) => s.settings.appLockEnabled)
  const [isLocked, setIsLocked] = useState(false)
  const hiddenAtRef = useRef<number | null>(null)

  useEffect(() => {
    if (!appLockEnabled) {
      setIsLocked(false)
      return
    }

    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now()
      } else if (document.visibilityState === 'visible' && hiddenAtRef.current) {
        const elapsed = Date.now() - hiddenAtRef.current
        hiddenAtRef.current = null
        if (elapsed >= LOCK_TIMEOUT_MS) {
          setIsLocked(true)
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [appLockEnabled])

  const unlock = useCallback(async (): Promise<boolean> => {
    // WebAuthn 미지원 시 바로 해제
    if (!window.PublicKeyCredential) {
      setIsLocked(false)
      return true
    }

    try {
      const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
      if (!available) {
        setIsLocked(false)
        return true
      }

      // 저장된 credential ID 조회
      const storedId = localStorage.getItem(CREDENTIAL_STORAGE_KEY)
      if (!storedId) {
        // credential이 없으면 잠금 해제 (등록 안 된 상태)
        setIsLocked(false)
        return true
      }

      // 등록된 credential로 생체인증 검증
      const credentialId = Uint8Array.from(atob(storedId), c => c.charCodeAt(0))
      const credential = await navigator.credentials.get({
        publicKey: {
          challenge: crypto.getRandomValues(new Uint8Array(32)),
          timeout: 60000,
          rpId: window.location.hostname,
          userVerification: 'required',
          allowCredentials: [{
            id: credentialId,
            type: 'public-key',
            transports: ['internal'],
          }],
        },
      })

      if (credential) {
        setIsLocked(false)
        return true
      }
    } catch {
      // 인증 실패 또는 취소
    }

    return false
  }, [])

  return { isLocked, unlock }
}

/**
 * WebAuthn credential 등록 — 설정에서 앱 잠금 활성화 시 호출
 * 지문/얼굴을 등록하고 credential ID를 localStorage에 저장
 */
export async function registerAppLockCredential(): Promise<boolean> {
  if (!window.PublicKeyCredential) return false

  try {
    const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
    if (!available) return false

    const userId = crypto.getRandomValues(new Uint8Array(16))

    const credential = await navigator.credentials.create({
      publicKey: {
        challenge: crypto.getRandomValues(new Uint8Array(32)),
        rp: {
          name: 'To-Do List',
          id: window.location.hostname,
        },
        user: {
          id: userId,
          name: 'app-lock',
          displayName: '앱 잠금',
        },
        pubKeyCredParams: [
          { type: 'public-key', alg: -7 },   // ES256
          { type: 'public-key', alg: -257 },  // RS256
        ],
        authenticatorSelection: {
          authenticatorAttachment: 'platform',
          userVerification: 'required',
          residentKey: 'preferred',
        },
        timeout: 60000,
      },
    }) as PublicKeyCredential | null

    if (credential) {
      // credential ID를 base64로 저장
      const idArray = new Uint8Array(credential.rawId)
      const idBase64 = btoa(String.fromCharCode(...idArray))
      localStorage.setItem(CREDENTIAL_STORAGE_KEY, idBase64)
      return true
    }
  } catch {
    // 등록 실패 또는 취소
  }

  return false
}

/**
 * 앱 잠금 credential 삭제 — 설정에서 비활성화 시 호출
 */
export function removeAppLockCredential(): void {
  localStorage.removeItem(CREDENTIAL_STORAGE_KEY)
}
